import type { DailyLog, DayProgress, HealthMetric, IsoDate } from "../domain/types.js";
import type { Role } from "../../shared/roles.js";
import type { DayMeasurement } from "../domain/measurement-view.js";
// 草稿的形狀跟著 AppUiState 走，所以留在 App.tsx。`import type` 會在編譯時
// 抹掉，這個互相參照不會產生執行期的循環相依（與 shared/types ↔ roles 同理）。
import type { BackfillDraft, BodyDraft, BodyPayload, StepsDraft } from "./App.js";
import { can } from "../../shared/roles.js";
import { Arrow } from "./Arrow.js";
import { fmt } from "../domain/numbers.js";
import { STEPS_GOAL, proteinMealCount, questRank, stepsDayDone } from "../domain/scoring.js";
import { workoutFor } from "../domain/workout.js";
import { bmiFrom, leanFrom } from "../domain/backfill.js";

/**
 * 這些元件的 DOM 結構與 class 名稱必須與改寫前完全一致——
 * styles.css 有 726 行全靠精確選取，對不上會靜默失效（不報錯、只是變醜）。
 * 基準截圖是唯一抓得到這種問題的防線。
 */

interface RankBadgeProps {
  progress: DayProgress;
}

function RankBadge({ progress }: RankBadgeProps) {
  const rank = questRank(progress);
  return (
    <section class={`quest-rank ${rank.cls}`} aria-label="今日任務評價">
      <div class="rank-badge"><span>RANK</span><strong>{rank.grade}</strong></div>
      <div class="rank-copy"><b>{rank.label}</b><small>{rank.message}</small></div>
      <div class="rank-score">{progress.done}/{progress.total}</div>
    </section>
  );
}

interface TaskProps {
  log: Partial<DailyLog>;
  /**
   * 監督者是 false。**打勾照舊渲染、只是停用**——勾起來沒有本身就是他要看的
   * 資訊，換成文字就看不出「哪三項做了」。DOM 結構也因此完全不變。
   *
   * 不計分的例行面板是唯一的例外，理由寫在那一塊上面。
   */
  canEdit: boolean;
  onToggle: (field: keyof DailyLog, next: boolean) => void;
}

function MealToggle({ log, canEdit, onToggle, field, label }: TaskProps & {
  field: "protein_breakfast_done" | "protein_lunch_done" | "protein_dinner_done";
  label: string;
}) {
  return (
    <label class="meal-toggle">
      <input
        type="checkbox"
        checked={!!log[field]}
        disabled={!canEdit}
        onChange={event => onToggle(field, (event.currentTarget as HTMLInputElement).checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function ManualTasks({ log, canEdit, onToggle }: TaskProps) {
  const proteinCount = proteinMealCount(log);
  return (
    <>
      <div class="core-task-grid">
        <label class="core-task wide">
          <input
            type="checkbox"
            checked={!!log.workout_done}
            disabled={!canEdit}
            onChange={event => onToggle("workout_done", (event.currentTarget as HTMLInputElement).checked)}
          />
          <span><b>課表完成</b><small>完成今天安排；休息日則完成步行或恢復</small></span>
        </label>

        <section class={`protein-task wide ${proteinCount === 3 ? "done" : ""}`}>
          <div class="protein-task-head">
            <span><b>三餐有蛋白質</b><small>每餐至少一份明確主蛋白，不用估克數</small></span>
            <strong>{proteinCount} / 3 餐</strong>
          </div>
          <div class="meal-toggles">
            <MealToggle log={log} canEdit={canEdit} onToggle={onToggle} field="protein_breakfast_done" label="早餐" />
            <MealToggle log={log} canEdit={canEdit} onToggle={onToggle} field="protein_lunch_done" label="午餐" />
            <MealToggle log={log} canEdit={canEdit} onToggle={onToggle} field="protein_dinner_done" label="晚餐" />
          </div>
        </section>

        <label class="core-task">
          <input
            type="checkbox"
            checked={!!log.vegetables_done}
            disabled={!canEdit}
            onChange={event => onToggle("vegetables_done", (event.currentTarget as HTMLInputElement).checked)}
          />
          <span><b>蔬菜至少 2 份</b><small>約兩個拳頭的熟蔬菜</small></span>
        </label>

        <label class="core-task">
          <input
            type="checkbox"
            checked={!!log.water_done}
            disabled={!canEdit}
            onChange={event => onToggle("water_done", (event.currentTarget as HTMLInputElement).checked)}
          />
          <span><b>水 ≥ 2.5L</b><small>大量流汗時再增加</small></span>
        </label>
      </div>

      {/*
        保健品只是提醒，不計入完成度與 Rank——所以整塊只給本人，是這個編輯器
        裡唯一不對監督者渲染的區塊。核心任務的打勾照舊渲染只加 disabled（勾了
        沒有本身就是他要看的資訊），這一塊反過來：品項名稱是私事，而遮掉又不
        會讓他少看到任何一項成績。

        伺服器端的 redactLogs 也會把 supplements_done 一起遮掉——這裡不渲染只是
        不邀請他看，擋不住直接打開 /api/daily-logs 的人。
      */}
      {canEdit && (
        <div class="routine-panel">
          <div><b>每日例行（不計分）</b><small>保健品是提醒，不影響完成度</small></div>
          <label>
            <input
              type="checkbox"
              checked={!!log.supplements_done}
              disabled={!canEdit}
              onChange={event => onToggle("supplements_done", (event.currentTarget as HTMLInputElement).checked)}
            />
            {" 合利他命、葉黃素、肌酸"}
          </label>
        </div>
      )}
    </>
  );
}

interface AutoTaskProps {
  date: IsoDate;
  metric: Partial<HealthMetric>;
  /** 本人才有。監督者連輸入框都不渲染，理由見下面。 */
  canBackfill: boolean;
  backfill: StepsDraft;
  /** 計畫期間內、今天以前沒有步數的日子。只有今日那一份會拿到非空陣列。 */
  missingSteps: IsoDate[];
  /** 這一天的補登面板展開了沒。 */
  open: boolean;
  onToggle: () => void;
  onBackfillSteps: (value: string) => void;
  onSaveSteps: () => void;
  onGoToDate: (date: IsoDate) => void;
}

/**
 * 步數：以**數值**而非打勾判定的核心任務。
 *
 * 數值通常由 Apple 健康同步寫入，捷徑不可用時本人可以手動補登——但補的是
 * 步數這個數字，**不是「已達標」這個狀態**。達標與否一律由 `stepsDayDone()`
 * 從數值推導，這裡沒有任何捷徑可以直接宣告完成。見 `CONTEXT.md` 的「自動
 * 任務」與「手動補登」。
 */
function AutoTask({
  date, metric, canBackfill, backfill, missingSteps, open, onToggle,
  onBackfillSteps, onSaveSteps, onGoToDate
}: AutoTaskProps) {
  const done = stepsDayDone(metric);
  const hasSteps = Number.isFinite(metric.steps_count);
  const value = hasSteps ? Math.round(metric.steps_count as number).toLocaleString("zh-TW") : "—";
  const percent = hasSteps ? Math.min(100, (metric.steps_count as number) / STEPS_GOAL * 100) : 0;

  // 草稿只屬於正在編輯的那一天。這個判斷不可省——DayEditor 會同時渲染兩次。
  const editing = backfill.date === date;
  const draft = editing ? backfill.value : (hasSteps ? String(Math.round(metric.steps_count as number)) : "");
  const saving = editing && backfill.status === "saving";
  const failed = editing && backfill.status === "error";

  // 空的、沒變、或正在送 → 沒有東西可存
  const unchanged = draft === (hasSteps ? String(Math.round(metric.steps_count as number)) : "");

  return (
    <div class="auto-health-grid single">
      <article class={`auto-health-card ${done ? "done" : ""}`}>
        <div><span class="auto-label">步數</span><b>今日步數</b></div>
        <strong>{value}<small> / {STEPS_GOAL.toLocaleString("zh-TW")}</small></strong>
        <div class="mini-track"><i style={{ width: `${percent}%` }} /></div>
        <small>
          {hasSteps
            ? (done ? "今日步數已達標" : "持續累積中")
            : "尚未收到步數，可手動補登"}
        </small>

        {/*
          監督者整塊不渲染，與保健品那塊同一條理由，而與核心任務打勾相反
          （那些是渲染但 disabled，因為「勾了沒有」本身就是他要看的資訊）。
          一個空的輸入框看起來像壞掉、或像他可以寫，而「有沒有一個輸入框」
          不是他要看的東西。
        */}
        {/*
          **預設收起來。** 補登是捷徑壞掉時的備援，不是每天要看的東西；
          常態下這張卡只要回答「今天走了幾步」就好。展開的狀態記在 ui 而不是
          這裡——這個 codebase 一支 hook 都沒有。
        */}
        {canBackfill && (
          <button type="button" class={`backfill-toggle ${open ? "open" : ""}`} onClick={onToggle}>
            {open ? "收起補登" : "手動補登步數"}
          </button>
        )}

        {canBackfill && open && (
          <div class="steps-backfill">
            <input
              type="number" inputMode="numeric" min="0" max="100000" step="1"
              aria-label="補登步數"
              placeholder="今天走了幾步"
              value={draft}
              disabled={saving}
              onInput={event => onBackfillSteps((event.currentTarget as HTMLInputElement).value)}
            />
            <button
              type="button"
              disabled={saving || unchanged || draft === ""}
              onClick={onSaveSteps}
            >
              {saving ? "儲存中…" : "儲存"}
            </button>
            <span class={`steps-backfill-msg ${failed ? "failed" : ""}`} aria-live="polite">
              {failed ? backfill.error : ""}
            </span>
          </div>
        )}

        {canBackfill && missingSteps.length > 0 && (
          <button type="button" class="steps-missing" onClick={() => onGoToDate(missingSteps[0])}>
            還有 {missingSteps.length} 天沒有步數 →
          </button>
        )}
      </article>
    </div>
  );
}

function NoMeasurement() {
  return <div class="no-measurement">這天尚無 InBody 測量資料；不影響課表紀錄。</div>;
}

/**
 * @param day 那一天的量測。`kind` 決定這裡給數字還是給箭頭——見
 *   `domain/measurement-view.ts`。
 */
function MeasurementMini({ day }: { day: DayMeasurement }) {
  /*
   * 遮蔽時三格都換成同一個箭頭——它回答的是「這天比前一筆是升是降」，
   * 那是一天一個的事實，不是每個欄位各一個。
   *
   * BMI 那一格整個不渲染：它與體重差一個固定的身高平方，起始體重已知就
   * 解得出身高，之後每天的 BMI 都能還原成公斤。它沒有非數字的呈現方式。
   *
   * class 與結構不動，只換內容（鐵則 7）。
   */
  if (day.kind === "shapeOnly") {
    if (!day.measured) return <NoMeasurement />;
    return (
      <div class="measurement-mini">
        <div>體重<b><Arrow direction={day.direction} /></b></div>
        <div>體脂<b><Arrow direction={day.direction} /></b></div>
        <div>淨體重<b><Arrow direction={day.direction} /></b></div>
      </div>
    );
  }

  const { measurement } = day;
  if (!measurement) return <NoMeasurement />;

  return (
    <>
      <div class="measurement-mini">
        <div>體重<b>{fmt(measurement.weight_kg)} kg</b></div>
        <div>體脂<b>{fmt(measurement.body_fat_percent)}%</b></div>
        <div>淨體重<b>{fmt(measurement.lean_body_mass_kg)} kg</b></div>
        <div>BMI<b>{fmt(measurement.bmi)}</b></div>
      </div>
      {/*
        來源標記只在不是 InBody 時出現——平常那一列全是同步進來的，天天掛
        一個「InBody」的標籤只是噪音。

        **不要做成有框的小標籤。** 這個編輯器裡的虛線框現在全都是「可以點的
        補登控制項」（`.backfill-toggle`、`.body-backfill`），而這一條是說明
        不是控制項——同一種框長在旁邊只會讓人去點它。所以它是一行純文字。

        這一塊只活在 revealed 分支：shapeOnly 的 source 已經被伺服器遮成空
        字串（見 shared/roles.ts），揭曉日之前監督者連「這天是他自己打的」
        都不知道。
      */}
      {measurement.source && measurement.source !== "InBody" && (
        <p class="measurement-source">來源：{measurement.source}</p>
      )}
    </>
  );
}

/**
 * 身體數值的補登。
 *
 * **只要你打體重與體脂兩格**，淨體重與 BMI 即時算出來，用 `placeholder`
 * 呈現而不是 `value`——灰字的意思天生就是「我沒填，系統會用這個」，換成
 * `value` 就會變成「看起來已經填好了，但其實是自動的」那種歧義。要覆寫
 * （InBody 的實測淨體重與公式算的可能差幾百克）就直接打字。
 *
 * 清空的語意兩組刻意不一樣，這是那種違反了不會報錯的東西：
 *
 * - **體重、體脂**：清空 = 送 `null`，把資料庫那一格清掉
 * - **淨體重、BMI**：清空 = 回到自動值
 *
 * 看起來不對稱，但兩邊都是唯一合理的解讀：前兩格是原始輸入，清空就是
 * 「這格我沒量」；後兩格有自動值兜底，清空只可能是「別覆寫了，用算的」。
 * 體重被清掉時自動值也算不出來，那時後兩格自然落到 `null`。
 */
function BodyBackfill({
  date, day, backfill, heightCm, open, onToggle, onBackfillBody, onSaveBody
}: {
  date: IsoDate;
  day: DayMeasurement;
  backfill: BodyDraft;
  heightCm: number;
  /** 這一天的補登面板展開了沒。預設收起來。 */
  open: boolean;
  onToggle: () => void;
  onBackfillBody: (field: "weight" | "fat" | "lean" | "bmi", value: string) => void;
  onSaveBody: (payload: BodyPayload) => void;
}) {
  const stored = day.kind === "revealed" ? day.measurement : null;
  const editing = backfill.date === date;
  const saving = editing && backfill.status === "saving";
  const failed = editing && backfill.status === "error";

  const text = (value: number | null | undefined) =>
    Number.isFinite(value as number) ? String(value) : "";

  const draft = {
    weight: editing ? backfill.weight : text(stored?.weight_kg),
    fat: editing ? backfill.fat : text(stored?.body_fat_percent),
    lean: editing ? backfill.lean : "",
    bmi: editing ? backfill.bmi : ""
  };

  const numberOrNull = (value: string) => (value.trim() === "" ? null : Number(value));
  const weight = numberOrNull(draft.weight);
  const fat = numberOrNull(draft.fat);
  const autoLean = leanFrom(weight, fat);
  const autoBmi = bmiFrom(weight, heightCm);

  // 覆寫優先；沒覆寫就用自動值。這一行就是上面那條清空語意的全部實作。
  const lean = draft.lean.trim() === "" ? autoLean : Number(draft.lean);
  const bmi = draft.bmi.trim() === "" ? autoBmi : Number(draft.bmi);

  const unchanged = !editing
    || (weight === (stored?.weight_kg ?? null) && fat === (stored?.body_fat_percent ?? null)
      && lean === (stored?.lean_body_mass_kg ?? null) && bmi === (stored?.bmi ?? null));

  /*
   * **體重與體脂沒有 placeholder。**
   *
   * 這張卡上的灰字只能有一個意思：「我沒填，系統會用這個」——那是淨體重與
   * BMI 的自動值。體重那格放 `78.2` 之類的示範值會讓同一種灰字同時代表
   * 「會被採用」與「只是舉例」，而 78.2 kg 看起來就很像那天真的有量。
   */
  const field = (
    key: "weight" | "fat" | "lean" | "bmi",
    label: string,
    placeholder: string,
    step: string
  ) => (
    <label>
      {label}
      <input
        type="number" inputMode="decimal" step={step} min="0"
        placeholder={placeholder}
        value={draft[key]}
        disabled={saving}
        onInput={event => onBackfillBody(key, (event.currentTarget as HTMLInputElement).value)}
      />
    </label>
  );

  if (!open) {
    return (
      <button type="button" class="backfill-toggle" onClick={onToggle}>
        {stored ? "手動修改量測" : "手動補登量測"}
      </button>
    );
  }

  return (
    <div class="body-backfill">
      <div class="body-backfill-grid">
        {field("weight", "體重 kg", "", "0.1")}
        {field("fat", "體脂 %", "", "0.1")}
        {field("lean", "淨體重 kg", autoLean === null ? "自動" : String(autoLean), "0.1")}
        {field("bmi", "BMI", autoBmi === null ? "自動" : String(autoBmi), "0.1")}
      </div>
      <div class="body-backfill-foot">
        <button
          type="button"
          disabled={saving || unchanged}
          onClick={() => onSaveBody({
            weight_kg: weight,
            body_fat_percent: fat,
            lean_body_mass_kg: lean,
            bmi
          })}
        >
          {saving ? "儲存中…" : "儲存量測"}
        </button>
        <button type="button" class="secondary" onClick={onToggle}>收起</button>
        <span class={`body-backfill-msg ${failed ? "failed" : ""}`} aria-live="polite">
          {failed ? backfill.error : "淨體重與 BMI 留空就用自動算的"}
        </span>
      </div>
    </div>
  );
}

/**
 * 監督者對這一天表達關心。一人一天一次，戳過就換成已戳過的樣子。
 *
 * 沒有暱稱就戳不了——後端會擋，但更重要的是這裡要說得出「去哪裡填」，
 * 否則按鈕就只是一個沒有理由的灰色按鈕。
 */
function CheerPanel({ nickname, cheered, onCheer, onNeedNickname }: {
  nickname: string;
  cheered: boolean;
  onCheer: () => void;
  onNeedNickname: () => void;
}) {
  /*
   * 還沒填名字時**不要停用按鈕**——停用的按鈕不會觸發 click，點下去完全
   * 沒有反應，而說明文字又要求你自己找到另一個分頁。改成點了就跳過去，
   * 並把游標放進輸入框。
   */
  const needsNickname = !nickname;
  return (
    <div class="cheer-panel">
      <div>
        <b>{cheered ? "你今天已經來看過他了" : "讓他知道你在看"}</b>
        <small>{nickname ? `以「${nickname}」的身分` : "還沒有名字，點旁邊就去填"}</small>
      </div>
      {/* DayEditor 會同時渲染兩次（今日與選取日），所以這裡不能用寫死的 id */}
      <button
        type="button" class={`cheer-btn ${cheered ? "cheered" : ""}`}
        disabled={cheered}
        onClick={needsNickname ? onNeedNickname : onCheer}
      >
        {cheered ? "已戳 ✓" : needsNickname ? "先取名字 →" : "戳一下"}
      </button>
    </div>
  );
}

export interface DayEditorProps {
  date: IsoDate;
  today: IsoDate;
  heading: string;
  log: Partial<DailyLog>;
  metric: Partial<HealthMetric>;
  /** 那天有沒有量、量了多少、是升是降。哪一個答得出來由 view 決定。 */
  day: DayMeasurement;
  progress: DayProgress;
  /** 能做什麼一律由角色查能力清單推導，不要在外面展開成一堆布林傳進來。 */
  role: Role;
  nickname: string;
  cheeredByMe: boolean;
  onToggle: (field: keyof DailyLog, next: boolean) => void;
  onNote: (value: string) => void;
  onCheer: () => void;
  /** 還沒有暱稱時按下戳一下：跳到戳戳樂分頁並聚焦輸入框。 */
  onNeedNickname: () => void;
  backfill: BackfillDraft;
  missingSteps: IsoDate[];
  /** 目前展開的補登面板，null 是全部收起來。 */
  openBackfill: { kind: "steps" | "body"; date: IsoDate } | null;
  onToggleBackfill: (kind: "steps" | "body") => void;
  onBackfillSteps: (value: string) => void;
  onSaveSteps: () => void;
  onBackfillBody: (field: "weight" | "fat" | "lean" | "bmi", value: string) => void;
  onSaveBody: (payload: BodyPayload) => void;
  /** BMI 要身高，而後端沒有它——推導在前端做。 */
  heightCm: number;
  onGoToDate: (date: IsoDate) => void;
}

export function DayEditor({
  date, today, heading, log, metric, day, progress,
  role, nickname, cheeredByMe, onToggle, onNote, onCheer, onNeedNickname,
  backfill, missingSteps, onBackfillSteps, onSaveSteps, onGoToDate,
  onBackfillBody, onSaveBody, heightCm, openBackfill, onToggleBackfill
}: DayEditorProps) {
  const [title, description] = workoutFor(date);
  const noteId = `note-${date}-${heading}`;
  const canEdit = can(role, "PUT", "/api/daily-logs");
  const canCheer = can(role, "POST", "/api/cheers");
  /*
   * 補登要另外查，**不能借用 canEdit**。能力清單以 (method, 路徑) 為單位就是
   * 為了這件事——借用別的路徑的答案等於把兩個能力綁死，之後想只收回其中一個
   * 就會發現做不到，而且不會有任何地方報錯。
   */
  const canBackfill = can(role, "PUT", "/api/daily-health");
  const canBackfillBody = can(role, "PUT", "/api/measurements");
  // 面板是不是這一天的這一塊。DayEditor 渲染兩次，只比 kind 會兩邊一起開。
  const openFor = (kind: "steps" | "body") =>
    openBackfill?.kind === kind && openBackfill.date === date;

  return (
    <>
      <div class="day-editor-head">
        <div><p class="eyebrow">DAILY QUEST</p><h2>{heading}</h2></div>
        <span class="day-editor-date">{date}{date === today ? "｜今天" : ""}</span>
      </div>

      <div class="plan-panel"><b>{title}</b><p>{description}</p></div>

      <MeasurementMini day={day} />
      {/* 監督者不渲染，理由與步數那塊、與保健品那塊相同 */}
      {canBackfillBody && (
        <BodyBackfill
          date={date} day={day} backfill={backfill.body} heightCm={heightCm}
          open={openFor("body")} onToggle={() => onToggleBackfill("body")}
          onBackfillBody={onBackfillBody} onSaveBody={onSaveBody}
        />
      )}

      <div class="editor-status">
        <strong>{progress.done} / {progress.total} 完成</strong>
        <div class="editor-progress"><i style={{ width: `${progress.percent}%` }} /></div>
      </div>

      <RankBadge progress={progress} />

      {canCheer && (
        <CheerPanel
          nickname={nickname} cheered={cheeredByMe}
          onCheer={onCheer} onNeedNickname={onNeedNickname}
        />
      )}
      <ManualTasks log={log} canEdit={canEdit} onToggle={onToggle} />
      <AutoTask
        date={date} metric={metric} canBackfill={canBackfill}
        backfill={backfill.steps} missingSteps={missingSteps}
        open={openFor("steps")} onToggle={() => onToggleBackfill("steps")}
        onBackfillSteps={onBackfillSteps} onSaveSteps={onSaveSteps} onGoToDate={onGoToDate}
      />

      {/* 改寫前這裡每次重繪都會重建 textarea，游標焦點會掉；
          Preact 的差異更新會保留節點。 */}
      <div class="editor-note">
        <label for={noteId}>備註</label>
        {canEdit ? (
          <textarea
            id={noteId}
            class="note"
            placeholder="疼痛、疲勞、飲食或今天的狀況…"
            value={log.note || ""}
            onInput={event => onNote((event.currentTarget as HTMLTextAreaElement).value)}
          />
        ) : (
          // 伺服器根本沒把備註送過來。這裡說明「有東西但看不到」，
          // 不要顯示成空的輸入框——那看起來像壞掉或像他可以寫。
          <p class="note-private">備註僅本人可見。</p>
        )}
      </div>
    </>
  );
}
